import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm, isDirty } from 'redux-form';
import { Link, Prompt } from 'react-router-dom';
import { connect, useSelector } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import moment from 'moment';

import { addCustomerAccount, getBasicCustomer, getBasicAccount, clearSelectedCustomer, clearSelectedAccount, getDefaultLegalEntity } from 'actions';

import withContainerErrorBoundary from 'components/ErrorBoundaries/ContainerErrorBoundary/withContainerErrorBoundary';
import AutoCompleteInput from 'components/Common/Inputs/AutoCompleteInput';
import Chapter3Status from 'common/Customers/Chapter3Status';
import Chapter4Status from 'common/Customers/Chapter4Status';
import EntityType from 'common/Customers/EntityType';
import { CheckboxInput, SelectInput, RenderDatePicker, NamePartsInput } from 'components/Common/Inputs';
import DateTimes from 'components/Common/DateTimes';

const ErrorItem = error => error && <li key={ error }>{error}</li>;

const ErrorToastr = errorList => (<div className="customer-account-form-confirm">
  <h1>Missing/incorrect data</h1>
  <hr />
  <p>
    {/* eslint-disable-next-line react/destructuring-assignment */}
    {errorList && errorList.errorList && errorList.errorList.map(ErrorItem)}
  </p></div>
);

const ConfirmationToaster = () => (
  <div className="customer-account-form-confirm">
    <h1>Are you sure?</h1>
    <hr />
    <p>Any entered data will be replaced. Do you want to proceed?</p>
  </div>
);

const CustomerSelectedToastr = () => (<div className="customer-account-form-confirm">
  <h1>Shareholder selected</h1>
  <hr />
  <p>Any entered presumption rule or additional data will be replaced.  Are you sure you want to proceed?</p>
</div>);

const ConfirmToastr = ({ form, useFullName }) => {
  const ch3Match = Chapter3Status.find(e => e.name === form.chapter3Status);
  const ch4Match = Chapter4Status.find(e => e.name === form.chapter4Status);

  return (<div className="customer-account-form-confirm">
    <h1>Review Shareholder &amp; Account Details</h1>
    <hr />
    <div className="columns">
      <div className="col-6 mb">
        <h2>Shareholder Data</h2>
        <div className="section-data">
          {useFullName
            ? (
              <div>
                <label>Name:</label><span>{form.customerName}</span>
              </div>
            ) : (
              <div>
                <label>First Name:</label><span>{form.firstName}</span>
                {form.middleName && <div><label>Middle Name:</label><span>{form.middleName}</span></div>}
                <label>Last Name:</label><span>{form.lastName}</span>
              </div>
            )}
          <label>Shareholder ID:</label><span className="col-7">{form.customerId}</span>
          <label>Shareholder Type:</label><span>{form.customerType === '0' ? 'Individual' : 'Entity'}</span>
        </div>
      </div>
      <div className="col-6 mb">
        <h2>Account Data</h2>
        <div className="section-data">
          <label>Name:</label><span>{form.accountName}</span>
          <label>Account Number:</label><span>{form.accountNumber}</span>
          <label>Legal Entity:</label><span>{form.legalEntity}</span>
        </div>
      </div>
      <div className="col-12 mb">
        <h2>Presumption Rule Data</h2>
        <div className="section-data">
          <label>Permanent Address Country:</label><span>{form.permanentAddressCountry ?? 'N/A'}</span>
          <label>Jurisdiction of Tax Residency:</label><span>{form.jurisdictionofTaxResidency ?? 'N/A'}</span>
          <label>Entity Type:</label><span>{EntityType.find(e => !Number.isNaN(form.entityType) && e.value === parseInt(form.entityType, 10))?.description ?? 'N/A'}</span>
          <label>Is Investor:</label><span>{form.isInvestor === 'true' ? 'Yes' : 'No'}</span>
        </div>
      </div>
      <div className="col-12 mb">
        <h2>Additional Data</h2>
        <div className="section-data columns">
          <div className="col-6">
            <label>Date of Birth:</label><span>{form.dateOfBirth}</span>
          </div>
          <div className="col-6" />
          <div className="col-6 mb">
            <label>Email Address:</label><span>{form.emailAddress}</span>
            <h3>Permanent Address</h3>
            <label>Line 1:</label><span>{form.permanentAddressLine1}</span>
            <label>Line 2:</label><span>{form.permanentAddressLine2}</span>
            <label>Line 3:</label><span>{form.permanentAddressLine3}</span>
            <label>Line 4:</label><span>{form.permanentAddressLine4}</span>
            <label>Country:</label><span>{form.permanentAddressCountry}</span>
            <label>Postal Code:</label><span>{form.permanentPostCode}</span>
          </div>
          <div className="col-6 mb">
            <label>Phone Number:</label><span>{form.phoneNumber}</span>
            <h3>Mailing Address</h3>
            <label>Line 1:</label><span>{form.mailingAddressLine1}</span>
            <label>Line 2:</label><span>{form.mailingAddressLine2}</span>
            <label>Line 3:</label><span>{form.mailingAddressLine3}</span>
            <label>Line 4:</label><span>{form.mailingAddressLine4}</span>
            <label>Country:</label><span>{form.mailingAddressCountry}</span>
            <label>Postal Code:</label><span>{form.mailingPostCode}</span>
          </div>
          <div className="col-6">
            <label>Chapter 3 Status:</label><span>{ch3Match ? `${ ch3Match.description } - ${ ch3Match.value }` : 'N/A'}</span>
          </div>
          <div className="col-6">
            <label>Chapter 4 Status:</label><span>{ch4Match ? `${ ch4Match.description } - ${ ch4Match.value }` : 'N/A'}</span>
          </div>
          <div className="col-4">
            <label>US TIN:</label><span>{form.usTin}</span>
          </div>
          <div className="col-4">
            <label>Foreign TIN:</label><span>{form.foreignTin}</span>
          </div>
          <div className="col-4">
            <label>GIIN:</label><span>{form.giin}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
  );
};

ConfirmToastr.propTypes = {
  form: PropTypes.shape().isRequired,
  useFullName: PropTypes.bool
};

ConfirmToastr.defaultProps = {
  useFullName: true
};

const AddCustomerAccount = (props) => {
  const [legalEntity, setLegalEntity] = useState();
  const [existingCustomer, setExistingCustomer] = useState();
  const [existingAccount, setExistingAccount] = useState();
  const [useFullName, setUseFullName] = useState(true);

  /* eslint-disable react/no-unstable-nested-components */
  const MemoizedErrorToastr = errorList => React.useCallback(<ErrorToastr errorList={ errorList } />, [errorList]);
  const MemoizedConfirmToastr = form => React.useCallback(<ConfirmToastr form={ form } useFullName={ useFullName } />, [form]);
  /* eslint-enable react/no-unstable-nested-components */

  const { dirty, change, handleSubmit, dispatch, state: { form }, state: { common: { jurisdictions } }, disableCustomer, disableAccount, existingLegalEntity } = props;
  const values = form.addCustomerAccount && form.addCustomerAccount.values;
  const allowTogglingCustomerDetails = values?.customerSelection !== 'existing';
  const { defaultLegalEntity } = useSelector(state => state.defaultLegalEntity);

  const entityTypes = EntityType.sort((a, b) => {
    if (a.description > b.description) return 1;
    return (b.description > a.description) ? -1 : 0;
  });

  const clearConfirm = () => {
    const elements = document.getElementById('add-confirm')?.getElementsByClassName('rrt-ok-btn');
    if (elements && elements.length > 0) {
      elements[0].click();
    }
  };

  const autoSelectLegalEntity = () => {
    if (defaultLegalEntity.count === 1) {
      const autoSelectedLegalEntity = defaultLegalEntity.legalEntity;
      setLegalEntity(autoSelectedLegalEntity);
    }
  };

  useEffect(() => () => {
    dispatch(clearSelectedAccount());
    dispatch(clearSelectedCustomer());
    setTimeout(() => clearConfirm(), 500);
  }, []);

  useEffect(() => {
    dispatch(getDefaultLegalEntity());
  }, []);

  useEffect(() => {
    change('customerId', '');
    change('customerName', '');
    change('firstName', '');
    change('middleName', '');
    change('lastName', '');
    if (defaultLegalEntity.count !== 1) {
      setLegalEntity(null);
    }
    setExistingCustomer(null);
    dispatch(clearSelectedCustomer());
  }, [values?.customerSelection]);

  useEffect(() => {
    if (values?.customerType === '1') {
      setUseFullName(true);
      change('firstName', '');
      change('middleName', '');
      change('lastName', '');
    } else {
      change('customerName', '');
    }
  }, [values?.customerType]);

  useEffect(() => {
    change('accountNumber', '');
    change('accountName', '');
    setExistingAccount(null);

    if (values?.accountSelection === 'existing') {
      setLegalEntity(null);
    } else if (defaultLegalEntity.count === 1) {
      autoSelectLegalEntity();
    } else {
      setLegalEntity(null);
    }

    dispatch(clearSelectedAccount());
  }, [values?.accountSelection]);

  useEffect(() => {
    if (useFullName) {
      change('firstName', '');
      change('middleName', '');
      change('lastName', '');
    } else {
      change('customerName', '');
    }
  }, [useFullName]);

  useEffect(() => {
    if (existingLegalEntity === null && defaultLegalEntity.count !== 1) {
      setLegalEntity(null);
    } else if (JSON.stringify(existingLegalEntity) !== JSON.stringify(legalEntity)) {
      if (existingLegalEntity !== null) {
        setLegalEntity(existingLegalEntity);
      }
    }
  }, [existingLegalEntity]);

  useEffect(() => {
    if (defaultLegalEntity.count === 1) {
      autoSelectLegalEntity();
    }
  }, [defaultLegalEntity]);

  function getExistingCustomer(autocompleteCustomer) {
    if (dirty) {
      const toastOptions = {
        component: CustomerSelectedToastr,
        onOk: () => {
          setExistingCustomer(autocompleteCustomer);
          dispatch(getBasicCustomer(autocompleteCustomer.id));
          change('presumptionRuleDataSelected', true);
          change('additionalDataSelected', true);
        },
        onCancel: () => {
          setExistingCustomer(null);
          dispatch(clearSelectedCustomer());
        }
      };
      toastr.confirm(null, toastOptions);
    }
  }

  function getExistingAccount(autocompleteAccount) {
    setExistingAccount(autocompleteAccount);
    dispatch(getBasicAccount(autocompleteAccount.id));
  }

  function permanentAddressPresent(currentValues) {
    return currentValues && (currentValues.permanentAddressLine1 || currentValues.permanentAddressLine2 || currentValues.permanentPostCode);
  }

  function mailingAddressPresent(currentValues) {
    return currentValues && (currentValues.mailingAddressLine1 || currentValues.mailingAddressLine2 || currentValues.mailingPostCode);
  }

  function clearLegalEntity() {
    if (legalEntity) {
      setLegalEntity(null);
    }
  }

  function clearPresumptionData(checked) {
    if (!checked
      && ((values.entityType !== '' && values.entityType != null) || values.permanentAddressCountry != null || values.jurisdictionofTaxResidency != null || values.isInvestor !== 'false')
    ) {
      const toastConfirmOptions = {
        component: ConfirmationToaster,
        onOk: () => {
          if (!permanentAddressPresent(values)) {
            change('permanentAddressCountry', null);
          }
          change('jurisdictionofTaxResidency', null);
          change('entityType', null);
          change('isInvestor', 'false');
        },
        onCancel: () => {
          change('presumptionRuleDataSelected', true);
        }
      };
      toastr.confirm(null, toastConfirmOptions);
    }
  }

  function clearAdditionalData(checked) {
    if (!checked
      && (values.dateOfBirth != null || values.emailAddress != null || values.phoneNumber != null || values.permanentAddressLine1 != null
        || values.mailingAddressLine1 != null || values.permanentAddressLine2 != null || values.mailingAddressLine2 != null || values.permanentAddressCountry != null
        || values.mailingAddressCountry != null || values.mailingPostCode != null || values.permanentPostCode != null || values.chapter3Status != null || values.chapter4Status != null
        || values.giin != null || values.foreignTin != null || values.usTin != null)
    ) {
      const toastConfirmOptions = {
        component: ConfirmationToaster,
        onOk: () => {
          change('emailAddress', null);
          change('permanentAddressLine1', null);
          change('permanentAddressLine2', null);
          change('permanentAddressLine3', null);
          change('permanentAddressLine4', null);
          if (!values || !values.presumptionRuleDataSelected) {
            change('permanentAddressCountry', null);
          }
          change('permanentPostCode', null);
          change('mailingAddressLine1', null);
          change('mailingAddressLine2', null);
          change('mailingAddressLine3', null);
          change('mailingAddressLine4', null);
          change('mailingAddressCountry', null);
          change('mailingPostCode', null);
          change('dateOfBirth', null);
          change('chapter3Status', null);
          change('chapter4Status', null);
          change('usTin', null);
          change('foreignTin', null);
          change('giin', null);
          change('phoneNumber', null);
        },
        onCancel: () => {
          change('additionalDataSelected', true);
        }
      };
      toastr.confirm(null, toastConfirmOptions);
    }
  }

  function onSubmit(newValues) {
    const charRanges = [
      '\u00a9',
      '\u00ae',
      '[\u2000-\u3300]',
      '\ud83c[\ud000-\udfff]',
      '\ud83d[\ud000-\udfff]',
      '\ud83e[\ud000-\udfff]'
    ];
    const charRegEx = new RegExp(charRanges.join('|'), 'g');

    const presumptionRuleDataRequired = newValues.presumptionRuleDataSelected === true && allowTogglingCustomerDetails;

    const errorList = [
      ...(!newValues.customerSelection ? ['Please select either New or Existing Shareholder.'] : []),
      ...(!newValues.accountSelection ? ['Please select either New, Existing or Generated Account.'] : []),
      ...(!newValues.customerId ? ['Shareholder Id is required.'] : []),
      ...(newValues?.customerId?.length > 100 ? ['Shareholder ID must not exceed 100 characters.'] : []),
      ...(charRegEx.test(newValues?.customerId) ? ['Shareholder ID contains invalid characters.'] : []),
      ...(!newValues.customerName && (!newValues.firstName && !newValues.lastName) ? ['Shareholder Name is required'] : []),
      ...(!newValues.firstName && newValues.lastName ? ['First Name is required'] : []),
      ...(newValues.firstName && !newValues.lastName ? ['Last Name is required'] : []),
      ...(newValues?.customerName?.length > 200 ? ['Shareholder Name must not exceed 200 characters.'] : []),
      ...(newValues?.firstName?.length > 100 ? ['First Name must not exceed 100 characters.'] : []),
      ...(newValues?.middleName?.length > 100 ? ['Middle Name must not exceed 100 characters.'] : []),
      ...(newValues?.lastName?.length > 100 ? ['Last Name must not exceed 100 characters.'] : []),
      ...(charRegEx.test(newValues?.customerName) ? ['Shareholder Name contains invalid characters.'] : []),
      ...(charRegEx.test(newValues?.firstName) ? ['First Name contains invalid characters.'] : []),
      ...(charRegEx.test(newValues?.middleName) ? ['Middle Name contains invalid characters.'] : []),
      ...(charRegEx.test(newValues?.lastName) ? ['Last Name contains invalid characters.'] : []),
      ...(!newValues.customerType ? ['Shareholder Type is required.'] : []),
      ...(!newValues.accountNumber && newValues.accountSelection !== 'generated' ? ['Account Number is required.'] : []),
      ...(newValues.accountNumber && newValues.accountNumber.length > 50 ? ['Account Number must not exceed 50 characters.'] : []),
      ...(newValues.accountNumber && charRegEx.test(newValues.accountNumber) ? ['Account Number contains invalid characters.'] : []),
      ...(!newValues.accountName && newValues.accountSelection !== 'generated' ? ['Account Name is required.'] : []),
      ...(newValues.accountSelection !== 'generated' && newValues.accountName && charRegEx.test(newValues.accountName) ? ['Account Name contains invalid characters.'] : []),
      ...(newValues.accountName && newValues.accountName.length > 200 ? ['Account Name must not exceed 200 characters.'] : []),
      ...(newValues.accountSelection !== 'existing' && !legalEntity?.id ? ['Please enter a Legal Entity'] : []),

      ...(presumptionRuleDataRequired
        && (!newValues.permanentAddressCountry
          || !newValues.jurisdictionofTaxResidency
          || !newValues.entityType) ? ['Data required to calculate presumption rules is missing'] : []),
      ...(((presumptionRuleDataRequired || permanentAddressPresent(newValues))
        && !newValues.permanentAddressCountry) ? ['Please enter a Permanent Address Country'] : []),
      ...(presumptionRuleDataRequired && !newValues.jurisdictionofTaxResidency ? ['Please select a Jurisdiction of Tax Residency'] : []),
      ...(presumptionRuleDataRequired && !newValues.entityType ? ['Please select an Entity Type'] : []),
      ...(newValues.emailAddress && newValues.emailAddress.length > 254 ? ['Email Address must not exceed 254 characters.'] : []),
      ...(newValues.permanentAddressLine1 && newValues.permanentAddressLine1.length > 300 ? ['Permanent Address Line 1 must not exceed 300 characters.'] : []),
      ...(newValues.permanentAddressLine1 && charRegEx.test(newValues.permanentAddressLine1) ? ['Permanent Address Line 1 contains invalid characters.'] : []),
      ...(newValues.permanentAddressLine2 && newValues.permanentAddressLine2.length > 300 ? ['Permanent Address Line 2 must not exceed 300 characters.'] : []),
      ...(newValues.permanentAddressLine2 && charRegEx.test(newValues.permanentAddressLine2) ? ['Permanent Address Line 2 contains invalid characters.'] : []),
      ...(newValues.permanentAddressLine3 && newValues.permanentAddressLine3.length > 300 ? ['Permanent Address Line 3 must not exceed 300 characters.'] : []),
      ...(newValues.permanentAddressLine3 && charRegEx.test(newValues.permanentAddressLine3) ? ['Permanent Address Line 3 contains invalid characters.'] : []),
      ...(newValues.permanentAddressLine4 && newValues.permanentAddressLine4.length > 300 ? ['Permanent Address Line 4 must not exceed 300 characters.'] : []),
      ...(newValues.permanentAddressLine4 && charRegEx.test(newValues.permanentAddressLine4) ? ['Permanent Address Line 4 contains invalid characters.'] : []),
      ...(newValues.permanentPostCode && newValues.permanentPostCode.length > 300 ? ['Permanent Address Post Code must not exceed 300 characters.'] : []),
      ...(newValues.permanentPostCode && charRegEx.test(newValues.permanentPostCode) ? ['Permanent Address Line 2 contains invalid characters.'] : []),
      ...(newValues.mailingAddressLine1 && newValues.mailingAddressLine1.length > 300 ? ['Mailing Address Line 1 must not exceed 300 characters.'] : []),
      ...(newValues.mailingAddressLine1 && charRegEx.test(newValues.mailingAddressLine1) ? ['Mailing Address Line 1 contains invalid characters.'] : []),
      ...(newValues.mailingAddressLine2 && newValues.mailingAddressLine2.length > 300 ? ['Mailing Address Line 2 must not exceed 300 characters.'] : []),
      ...(newValues.mailingAddressLine2 && charRegEx.test(newValues.mailingAddressLine2) ? ['Mailing Address Line 2 contains invalid characters.'] : []),
      ...(newValues.mailingAddressLine3 && newValues.mailingAddressLine3.length > 300 ? ['Mailing Address Line 3 must not exceed 300 characters.'] : []),
      ...(newValues.mailingAddressLine3 && charRegEx.test(newValues.mailingAddressLine3) ? ['Mailing Address Line 3 contains invalid characters.'] : []),
      ...(newValues.mailingAddressLine4 && newValues.mailingAddressLine4.length > 300 ? ['Mailing Address Line 4 must not exceed 300 characters.'] : []),
      ...(newValues.mailingAddressLine4 && charRegEx.test(newValues.mailingAddressLine4) ? ['Mailing Address Line 4 contains invalid characters.'] : []),
      ...(mailingAddressPresent(newValues) && !newValues.mailingAddressCountry ? ['Please enter a Mailing Address Country'] : []),
      ...(newValues.mailingPostCode && newValues.mailingPostCode.length > 300 ? ['Mailing Address Post Code must not exceed 300 characters.'] : []),
      ...(newValues.mailingPostCode && charRegEx.test(newValues.mailingPostCode) ? ['Mailing Address Post Code contains invalid characters.'] : []),
      ...(newValues.phoneNumber && newValues.phoneNumber.length > 30 ? ['Phone Number must not exceed 30 characters.'] : []),
      ...(newValues.phoneNumber && charRegEx.test(newValues.phoneNumber) ? ['Phone Number contains invalid characters.'] : []),

      ...(newValues.usTin && newValues.usTin.length > 40 ? ['US TIN must not exceed 40 characters.'] : []),
      ...(newValues.usTin && charRegEx.test(newValues.usTin) ? ['US TIN contains invalid characters.'] : []),
      ...(newValues.foreignTin && newValues.foreignTin.length > 40 ? ['Foreign TIN must not exceed 60 characters.'] : []),
      ...(newValues.foreignTin && charRegEx.test(newValues.foreignTin) ? ['Foreign TIN contains invalid characters.'] : []),
      ...(newValues.giin && newValues.giin.length > 60 ? ['GIIN must not exceed 60 characters.'] : []),
      ...(newValues.giin && charRegEx.test(newValues.giin) ? ['GIIN contains invalid characters.'] : []),
    ];

    /* eslint-disable react/no-unstable-nested-components */
    let toastOptions = null;
    if (errorList && errorList.length > 0) {
      toastOptions = {
        disableCancel: true,
        component: () => MemoizedErrorToastr(errorList)
      };
    } else {
      Object.assign(newValues, { legalEntityId: legalEntity?.id ?? 0, legalEntity: legalEntity?.value ?? '', existingCustomerId: existingCustomer?.id ?? 0, existingAccountId: existingAccount?.id ?? 0 });
      toastOptions = {
        onOk: () => dispatch(addCustomerAccount(newValues)),
        component: () => MemoizedConfirmToastr(newValues)
      };
    }
    /* eslint-enable react/no-unstable-nested-components */

    toastr.confirm(null, toastOptions);
  }

  return (
    <form onSubmit={ handleSubmit(onSubmit) } className="customer-account-page">
      <Prompt
        when={ dirty }
        message="Are you sure you want to leave this page?"
      />
      <h1>Create New Shareholder / Account</h1>
      <div>
        <div className="panel-col">
          <div className="customer-account-panel">
            <div className="panel p-2">
              <div className="panel-header">
                <h2>Shareholder Data</h2>
              </div>
              <div className="panel-data top-panel">
                <div className="record-radio-options">
                  <label className="form-radio panel-row">
                    <Field name="customerSelection" component="input" type="radio" value="new" />
                    <i className="form-icon" /> Create New Shareholder
                  </label>
                  <label className="form-radio panel-row">
                    <Field name="customerSelection" component="input" type="radio" value="existing" />
                    <i className="form-icon" /> Select Existing Shareholder
                  </label>
                </div>
                <div className="form-group columns">
                  <label className="form-label col-5">Shareholder ID *</label>
                  <Field
                    className="form-input col-6"
                    name="customerId"
                    component="input"
                    type="text"
                    placeholder="Enter shareholder ID"
                    disabled={ disableCustomer }
                  />
                </div>
                <div className="form-group columns radio-options">
                  <label className="form-label col-5">Shareholder Type *</label>
                  <label className="form-checkbox panel-row">
                    <Field name="customerType" component="input" type="radio" value="0" disabled={ disableCustomer } />
                    <i className="form-icon" id="customer-type-individual-checkbox" /> Individual
                  </label>
                  <label className="form-checkbox panel-row">
                    <Field name="customerType" component="input" type="radio" value="1" disabled={ disableCustomer } />
                    <i className="form-icon" id="customer-type-entity-checkbox" /> Entity
                  </label>
                </div>
                <div className="form-group columns">
                  <label className="form-label col-5">Shareholder Name *</label>
                  { values && values.customerType === '1' ? (
                    <Field
                      className="form-input col-6"
                      name="customerName"
                      component="input"
                      type="text"
                      placeholder="Enter customer name"
                      disabled={ disableCustomer }
                    />
                  ) : (
                    <NamePartsInput
                      className="customer-account-name-parts-input"
                      disableFields={ disableCustomer }
                      useFullNameExternal={ useFullName }
                      setUseFullNameExternal={ setUseFullName }
                    />
                  ) }
                </div>
                <div className={ values && values.customerSelection === 'existing' ? 'form-group columns' : 'hide' }>
                  <label className="form-label col-5">Existing Shareholder *</label>
                  <div className="col-6">
                    <AutoCompleteInput
                      id="existingCustomer"
                      name="existingCustomer"
                      category="customer"
                      placeholder="Enter shareholder..."
                      value={ existingCustomer || '' }
                      onSelectedItem={ lookupValue => getExistingCustomer(lookupValue) }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="panel-col">
          <div className="customer-account-panel">
            <div className="panel p-2">
              <div className="panel-header">
                <h2>Account Data</h2>
              </div>
              <div className="panel-data top-panel">
                <div className="record-radio-options">
                  <label className="form-radio panel-row">
                    <Field name="accountSelection" component="input" type="radio" value="generated" />
                    <i className="form-icon" /> Auto-Generated Account
                  </label>
                  <label className="form-radio panel-row">
                    <Field name="accountSelection" component="input" type="radio" value="new" />
                    <i className="form-icon" /> Create New Account
                  </label>
                  <label className="form-radio panel-row">
                    <Field name="accountSelection" component="input" type="radio" value="existing" />
                    <i className="form-icon" /> Select Existing Account
                  </label>
                </div>
                <div className={ values && values.accountSelection !== 'generated' ? 'form-group columns' : 'hide' }>
                  <label className="form-label col-5">Account Number *</label>
                  <Field
                    className="form-input col-6"
                    name="accountNumber"
                    component="input"
                    type="text"
                    placeholder="Enter account number"
                    disabled={ disableAccount }
                  />
                </div>
                <div className={ values && values.accountSelection !== 'generated' ? 'form-group columns' : 'hide' }>
                  <label className="form-label col-5">Account Name *</label>
                  <Field
                    className="form-input col-6"
                    name="accountName"
                    component="input"
                    type="text"
                    placeholder="Enter account name"
                    disabled={ disableAccount }
                  />
                </div>
                <div className="form-group columns">
                  <label className="form-label col-5">Legal Entity *</label>
                  <div className="col-6">
                    <AutoCompleteInput
                      id="legalEntity"
                      name="legalEntity"
                      category="legalentity"
                      placeholder="Enter legal entity..."
                      value={ legalEntity || '' }
                      disabled={ disableAccount || defaultLegalEntity.count === 1 }
                      onChange={ () => clearLegalEntity() }
                      onSelectedItem={ lookupValue => setLegalEntity(lookupValue) }
                    />
                  </div>
                </div>
                <div className={ values && values.accountSelection === 'generated' ? 'form-group columns' : 'hide' }>
                  <div className="col-5">
                    <label className="form-label adjacent-addon">Populate Account ID using *</label>
                    <span className="form-label-addon">Account Name will be populated with Shareholder Name</span>
                  </div>
                  <div className="col-6">
                    <label className="form-checkbox panel-row">
                      <Field name="populateAccountIdUsing" component="input" type="radio" value="customerId" />
                      <i className="form-icon" /> Shareholder ID
                    </label>
                    <label className="form-checkbox panel-row">
                      <Field name="populateAccountIdUsing" component="input" type="radio" value="customerIdLegalEntity" />
                      <i className="form-icon" /> Shareholder ID / Legal Entity
                    </label>
                  </div>
                </div>
                <div className={ values && values.accountSelection === 'existing' ? 'form-group columns' : 'hide' }>
                  <label className="form-label col-5">Account *</label>
                  <div className="col-6">
                    <AutoCompleteInput
                      id="existingAccount"
                      name="existingAccount"
                      category="account"
                      placeholder="Enter account..."
                      value={ existingAccount || '' }
                      onSelectedItem={ lookupValue => getExistingAccount(lookupValue) }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="customer-account-panel">
          <div className="panel p-2">
            <div className={ `panel-header ${ values && values.presumptionRuleDataSelected === true ? '' : 'no-border' }` }>
              <h2 className="col-7">Presumption Rule Data</h2>
              {allowTogglingCustomerDetails && <div className="col-5 section-expand-collapse">
                <i>tick to enter details</i>
                <Field
                  name="presumptionRuleDataSelected"
                  component={ CheckboxInput }
                  type="text"
                  iValue="true"
                  onChange={ e => clearPresumptionData(e.target.checked) }
                />
              </div>}
            </div>
            <div className={ values && values.presumptionRuleDataSelected === true ? 'panel-data' : 'hide' }>
              <div>
                <div className="form-group columns">
                  <label className="form-label col-2">Permanent Address Country *</label>
                  <span className="col-6">
                    <Field
                      className="form-input"
                      name="permanentAddressCountry"
                      value={ values && (values.permanentAddressCountry ?? '') }
                      component={ SelectInput }
                      type="text"
                      disabled={ disableCustomer }
                    >
                      <option value="">{disableCustomer ? '' : 'Please select...'}</option>
                      {jurisdictions && jurisdictions.map(x => (
                        <option key={ x.name }>{ x.name }</option>
                      ))}
                    </Field>
                  </span>
                </div>
                <div className="form-group columns">
                  <label className="form-label col-2">Jurisdiction of Tax Residency *</label>
                  <span className="col-6">
                    <Field
                      className="form-input"
                      name="jurisdictionofTaxResidency"
                      value={ values && (values.jurisdictionofTaxResidency ?? '') }
                      component={ SelectInput }
                      type="text"
                      disabled={ disableCustomer }
                    >
                      <option value="">{disableCustomer ? '' : 'Please select...'}</option>
                      {jurisdictions && jurisdictions.map(x => (
                        <option key={ x.name }>{ x.name }</option>
                      ))}
                    </Field>
                  </span>
                </div>
                <div className="form-group columns">
                  <label className="form-label col-2">Entity Type *</label>
                  <span className="col-6">
                    <Field
                      className="form-input"
                      name="entityType"
                      value={ values && (values.entityType ?? '') }
                      component={ SelectInput }
                      type="text"
                      disabled={ disableCustomer }
                    >
                      <option value="">{disableCustomer ? '' : 'Please select...'}</option>
                      {entityTypes.map(x => (
                        <option key={ x.value } value={ x.value }>{x.description}</option>
                      ))}
                    </Field>
                  </span>
                </div>
                <div className="form-group columns radio-options">
                  <label className="form-label col-2">Is Investor *</label>
                  <label className="form-checkbox panel-row">
                    <Field name="isInvestor" component="input" type="radio" value="true" disabled={ disableCustomer } />
                    <i className="form-icon" /> Yes
                  </label>
                  <label className="form-checkbox panel-row">
                    <Field name="isInvestor" component="input" type="radio" value="false" disabled={ disableCustomer } />
                    <i className="form-icon" /> No
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="customer-account-panel">
          <div className="panel p-2">
            <div className={ `panel-header ${ values && values.additionalDataSelected === true ? '' : 'no-border' }` }>
              <h2 className="col-7">Additional Data</h2>
              {allowTogglingCustomerDetails && <div className="col-5 section-expand-collapse">
                <i>tick to enter details</i>
                <Field
                  name="additionalDataSelected"
                  component={ CheckboxInput }
                  type="text"
                  iValue="true"
                  onChange={ e => clearAdditionalData(e.target.checked) }
                />
              </div>}
            </div>
            <div className={ values && values.additionalDataSelected === true ? 'panel-data' : 'hide' }>
              <div className="columns">
                <div className="col-12 mb-2rem">
                  <div className="form-group">
                    <div className="col-6">
                      <div className="columns">
                        <label className="form-label col-4">Date of Birth </label>
                        <span className="col-7">
                          {!disableCustomer && <Field
                            component={ RenderDatePicker }
                            name="dateOfBirth"
                            className="form-input panel-field"
                            placeholder="MMM DD, YYYY"
                            dateFormat="MMM dd, yyyy"
                            { ... {
                              maxDate: DateTimes.today,
                            } }
                            value={ values && values.dateOfBirth }
                          />}
                          {disableCustomer && <Field
                            className="form-input panel-field"
                            name="dateOfBirth"
                            component="input"
                            type="text"
                            disabled
                          />}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-6">
                  <div className="form-group columns">
                    <label className="form-label col-4">Email Address </label>
                    <Field
                      className="form-input col-7"
                      name="emailAddress"
                      component="input"
                      type="text"
                      disabled={ disableCustomer }
                    />
                  </div>
                  <div className="form-group mt-2rem columns">
                    <label className="col-12 bold">Permanent Address</label>
                    <label className="form-label col-4">Line 1 </label>
                    <Field
                      className="form-input col-7"
                      name="permanentAddressLine1"
                      component="input"
                      type="text"
                      disabled={ disableCustomer }
                    />
                  </div>
                  <div className="form-group columns">
                    <label className="form-label col-4">Line 2 </label>
                    <Field
                      className="form-input col-7"
                      name="permanentAddressLine2"
                      component="input"
                      type="text"
                      disabled={ disableCustomer }
                    />
                  </div>
                  <div className="form-group columns">
                    <label className="form-label col-4">Line 3 </label>
                    <Field
                      className="form-input col-7"
                      name="permanentAddressLine3"
                      component="input"
                      type="text"
                      disabled={ disableCustomer }
                    />
                  </div>
                  <div className="form-group columns">
                    <label className="form-label col-4">Line 4 </label>
                    <Field
                      className="form-input col-7"
                      name="permanentAddressLine4"
                      component="input"
                      type="text"
                      disabled={ disableCustomer }
                    />
                  </div>
                  <div className="form-group columns">
                    <label className="form-label col-4">Country </label>
                    <span className="col-7">
                      <Field
                        className="form-input"
                        name="permanentAddressCountry"
                        value={ values && (values.permanentAddressCountry ?? '') }
                        component={ SelectInput }
                        type="text"
                        disabled={ disableCustomer }
                      >
                        <option value="">{disableCustomer ? '' : 'Please select...'}</option>
                        {jurisdictions && jurisdictions.map(x => (
                          <option key={ x.name }>{ x.name }</option>
                        ))}
                      </Field>
                    </span>
                  </div>
                  <div className="form-group columns">
                    <label className="form-label col-4">Post Code </label>
                    <Field
                      className="form-input col-7"
                      name="permanentPostCode"
                      component="input"
                      type="text"
                      disabled={ disableCustomer }
                    />
                  </div>
                  <div className="form-group mt-2rem columns">
                    <label className="form-label col-4">Chapter 3 Status </label>
                    <span className="col-7">
                      <Field
                        className="form-input"
                        name="chapter3Status"
                        value={ values && (values.chapter3Status ?? '') }
                        component={ SelectInput }
                        type="text"
                        disabled={ disableCustomer }
                      >
                        <option value="">{disableCustomer ? '' : 'Please select'}</option>
                        {Chapter3Status.map(x => (
                          <option key={ x.value } value={ x.name }>{x.description} - {x.value}</option>
                        ))}
                      </Field>
                    </span>
                  </div>
                </div>
                <div className="col-6">
                  <div className="form-group columns">
                    <label className="form-label col-4">Phone Number </label>
                    <Field
                      className="form-input col-7"
                      name="phoneNumber"
                      component="input"
                      type="text"
                      disabled={ disableCustomer }
                    />
                  </div>
                  <div className="form-group mt-2rem columns">
                    <label className="col-12 bold">Mailing Address</label>
                    <label className="form-label col-4">Line 1 </label>
                    <Field
                      className="form-input col-7"
                      name="mailingAddressLine1"
                      component="input"
                      type="text"
                      disabled={ disableCustomer }
                    />
                  </div>
                  <div className="form-group columns">
                    <label className="form-label col-4">Line 2 </label>
                    <Field
                      className="form-input col-7"
                      name="mailingAddressLine2"
                      component="input"
                      type="text"
                      disabled={ disableCustomer }
                    />
                  </div>
                  <div className="form-group columns">
                    <label className="form-label col-4">Line 3 </label>
                    <Field
                      className="form-input col-7"
                      name="mailingAddressLine3"
                      component="input"
                      type="text"
                      disabled={ disableCustomer }
                    />
                  </div>
                  <div className="form-group columns">
                    <label className="form-label col-4">Line 4 </label>
                    <Field
                      className="form-input col-7"
                      name="mailingAddressLine4"
                      component="input"
                      type="text"
                      disabled={ disableCustomer }
                    />
                  </div>
                  <div className="form-group columns">
                    <label className="form-label col-4">Country </label>
                    <span className="col-7">
                      <Field
                        className="form-input"
                        name="mailingAddressCountry"
                        value={ values && (values.mailingAddressCountry ?? '') }
                        component={ SelectInput }
                        type="text"
                        disabled={ disableCustomer }
                      >
                        <option value="">{disableCustomer ? '' : 'Please select...'}</option>
                        {jurisdictions && jurisdictions.map(x => (
                          <option key={ x.name }>{ x.name }</option>
                        ))}
                      </Field>
                    </span>
                  </div>
                  <div className="form-group columns">
                    <label className="form-label col-4">Post Code </label>
                    <Field
                      className="form-input col-7"
                      name="mailingPostCode"
                      component="input"
                      type="text"
                      disabled={ disableCustomer }
                    />
                  </div>
                  <div className="form-group mt-2rem columns">
                    <label className="form-label col-4">Chapter 4 Status </label>
                    <span className="col-7">
                      <Field
                        className="form-input"
                        name="chapter4Status"
                        value={ values && (values.chapter4Status ?? '') }
                        component={ SelectInput }
                        type="text"
                        disabled={ disableCustomer }
                      >
                        <option value="">{disableCustomer ? '' : 'Please select'}</option>
                        {Chapter4Status.map(x => (
                          <option key={ x.value } value={ x.name }>{x.description} - {x.value}</option>
                        ))}
                      </Field>
                    </span>
                  </div>
                </div>
                <div className="col-12">
                  <div className="columns mt-2rem">
                    <div className="col-4">
                      <div className="form-group columns">
                        <label className="form-label col-3">US TIN </label>
                        <Field
                          className="form-input col-7"
                          name="usTin"
                          component="input"
                          type="text"
                          disabled={ disableCustomer }
                        />
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="form-group columns">
                        <label className="form-label col-3">Foreign TIN </label>
                        <Field
                          className="form-input col-7"
                          name="foreignTin"
                          component="input"
                          type="text"
                          disabled={ disableCustomer }
                        />
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="form-group columns">
                        <label className="form-label col-3">GIIN </label>
                        <Field
                          className="form-input col-7"
                          name="giin"
                          component="input"
                          type="text"
                          disabled={ disableCustomer }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="customer-account-panel">
          <div className="panel">
            <div className="buttons">
              <Link to="/" className="btn btn-secondary">Cancel</Link>
              <button type="submit" className="btn btn-primary">Create Record</button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

AddCustomerAccount.propTypes = {
  change: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  state: PropTypes.any.isRequired,
  disableCustomer: PropTypes.bool.isRequired,
  disableAccount: PropTypes.bool.isRequired,
  existingLegalEntity: PropTypes.any,
  dirty: PropTypes.bool.isRequired
};

AddCustomerAccount.defaultProps = {
  existingLegalEntity: null
};

function mapStateToProps(state) {
  let initialValues = {
    customerType: '0',
    presumptionRuleDataSelected: 'false',
    additionalDataSelected: 'false',
    isInvestor: 'false',
    legalEntity: '',
    entityType: '',
    accountSelection: 'generated',
    populateAccountIdUsing: 'customerId'
  };

  const disableCustomer = typeof state.form?.addCustomerAccount?.values?.customerSelection === 'undefined'
    || state.form?.addCustomerAccount?.values?.customerSelection === 'existing';
  const disableAccount = typeof state.form?.addCustomerAccount?.values?.accountSelection === 'undefined'
    || state.form?.addCustomerAccount?.values?.accountSelection === 'existing';
  let existingLegalEntity = null;
  const dirty = isDirty('addCustomerAccount')(state);

  if (state.form?.addCustomerAccount?.values?.customerSelection === 'existing' && state.customer?.basicCustomer) {
    initialValues = state.form.addCustomerAccount.values;

    const { customer: { basicCustomer } } = state;

    Object.assign(initialValues, {
      customerId: basicCustomer.customerId,
      customerName: basicCustomer.customerName,
      customerType: basicCustomer.customerType === 1 ? '1' : '0',
      permanentAddressCountry: basicCustomer.permanentAddressCountry,
      jurisdictionofTaxResidency: basicCustomer.jurisdictionofTaxResidency,
      entityType: basicCustomer.entityType,
      isInvestor: basicCustomer.isInvestor ? 'true' : 'false',
      emailAddress: basicCustomer.emailAddress,
      permanentAddressLine1: basicCustomer.permanentAddressLine1,
      permanentAddressLine2: basicCustomer.permanentAddressLine2,
      permanentPostCode: basicCustomer.permanentPostCode,
      mailingAddressLine1: basicCustomer.mailingAddressLine1,
      mailingAddressLine2: basicCustomer.mailingAddressLine2,
      mailingAddressCountry: basicCustomer.mailingAddressCountry,
      mailingPostCode: basicCustomer.mailingPostCode,
      dateOfBirth: basicCustomer.dateOfBirth ? moment(basicCustomer.dateOfBirth).format('MMM DD, YYYY') : '',
      phoneNumber: basicCustomer.phoneNumber,
      chapter3Status: basicCustomer.chapter3Status,
      chapter4Status: basicCustomer.chapter4Status,
      usTin: basicCustomer.usTin,
      foreignTin: basicCustomer.foreignTin,
      giin: basicCustomer.giin
    });
  }

  if (state.form?.addCustomerAccount?.values?.accountSelection === 'existing' && state.customer?.basicAccount) {
    initialValues = state.form.addCustomerAccount.values;

    const { customer: { basicAccount } } = state;

    Object.assign(initialValues, {
      accountNumber: basicAccount.accountNumber,
      accountName: basicAccount.accountName
    });

    const legalEntityValue = `${ basicAccount.legalEntity.identifier } - ${ basicAccount.legalEntity.name }`;

    existingLegalEntity = {
      id: basicAccount.legalEntity.id,
      type: 'legalentity',
      value: legalEntityValue,
      chapter3Status: basicAccount.legalEntity.chapter3Status,
      chapter4Status: basicAccount.legalEntity.chapter4Status,
      crsParticipating: basicAccount.legalEntity.crsParticipating,
      carfParticipating: basicAccount.legalEntity.carfParticipating,
      chapter4StatusDescription: basicAccount.legalEntity.chapter4StatusDescription,
      chapter3StatusDescription: basicAccount.legalEntity.chapter3StatusDescription
    };
  }

  if (state?.form?.addCustomerAccount?.values) {
    const { values: { customerSelection, accountSelection } } = state.form.addCustomerAccount;
    if (customerSelection || accountSelection) {
      Object.assign(initialValues, { customerSelection, accountSelection });
    }
  }

  return {
    state,
    initialValues,
    disableCustomer,
    disableAccount,
    existingLegalEntity,
    dirty
  };
}

const mapDispatchToProps = dispatch => ({ dispatch });

const AddCustomerAccountForm = reduxForm({
  form: 'addCustomerAccount'
})(AddCustomerAccount);

export default connect(mapStateToProps, mapDispatchToProps)(withContainerErrorBoundary(AddCustomerAccountForm, 'AddCustomerAccount'));
